import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus, Minus } from 'lucide-react'

// Keep in sync with the home-faq JSON-LD in index.html.
const faqs = [
  {
    q: 'What is Statbox?',
    a: 'Statbox is an AI sports app that turns video of your own football, basketball, and padel games into player stats, highlight clips, and replays.',
  },
  {
    q: 'How does the AI create stats and highlights?',
    a: 'AI video analysis drafts each player’s stats and cuts their highlights from the match recording. A Statbox analyst reviews the game before the report is delivered to the app.',
  },
  {
    q: 'What football stats do I get?',
    a: 'Goals, assists, shots on and off target, chances created, dribbles, tackles, and saves, plus a highlight clip for every player. It works for 5-a-side up to 11-a-side.',
  },
  {
    q: 'Is there a Statbox app?',
    a: 'Yes. The Statbox app is free to download on iOS and Android. Players use it to join games, open their stats, and share their highlights.',
  },
  {
    q: 'Do I need to buy equipment?',
    a: 'No. Play at a Statbox-enabled venue, or film the game yourself and upload the MP4.',
  },
  {
    q: 'Which sports do you support?',
    a: 'Football, basketball, padel. More coming.',
  },
  {
    q: 'How much does it cost?',
    a: "Depends on your community. We'll tailor it on the call.",
  },
  {
    q: 'How long does setup take?',
    a: 'Most communities run their first game within a week.',
  },
]

export const FAQSection = () => {
  const [open, setOpen] = useState<number | null>(0)

  return (
    <section id="faqs" className="flex-1 min-h-0 flex flex-col pt-28 sm:pt-32 lg:pt-24 pb-16 sm:pb-20 lg:pb-14 bg-black">
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-10 w-full flex-1 flex flex-col justify-center min-h-0">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-6 sm:mb-10 lg:mb-4"
        >
          <h2 className="text-3xl sm:text-3xl md:text-4xl lg:text-3xl font-bold text-white mb-2 sm:mb-3">Statbox FAQs</h2>
          <p className="text-[#999] text-base sm:text-base lg:text-base">
            Everything you need to know about Statbox.
          </p>
        </motion.div>

        <div className="border border-[#1a1a1a] rounded-xl overflow-hidden">
          {faqs.map((faq, i) => (
            <div key={i} className={i > 0 ? 'border-t border-[#1a1a1a]' : ''}>
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 lg:py-3 text-left group min-h-[48px] lg:min-h-[44px] touch-manipulation"
              >
                <span className={`text-base sm:text-[16px] font-medium pr-4 transition-colors ${open === i ? 'text-white' : 'text-[#ccc] group-hover:text-white'}`}>
                  {faq.q}
                </span>
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#111] border border-[#222] flex items-center justify-center">
                  {open === i ? <Minus className="w-3.5 h-3.5 text-[#888]" /> : <Plus className="w-3.5 h-3.5 text-[#888]" />}
                </span>
              </button>
              <AnimatePresence initial={false}>
                {open === i ? (
                  <motion.div
                    key="open"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <p className="text-sm sm:text-[15px] lg:text-sm text-[#888] leading-relaxed px-4 sm:px-6 pb-3 sm:pb-4 lg:pb-3">
                      {faq.a}
                    </p>
                  </motion.div>
                ) : (
                  <p key="closed" hidden>{faq.a}</p>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
